import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import ClassOverModal from '../../components/ClassOverModal';
import { useAdminOrCoordinatorSession } from '../../context/TrackerContext';
import { getAdminMentors, getStudents } from '../../lib/api';
import type { StudentFull } from '../../lib/api';

const initials = (name: string) => name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

// ── Main Page ─────────────────────────────────────────────────────────────────
// Student Coordinators look after every student, so they raise Class Over from
// the full list of active students (mentors raise it from My Students). The
// student is made inactive straight away and admin is emailed the class report.

const CoordinatorClassOver: React.FC = () => {
  const { adminPassword } = useAdminOrCoordinatorSession();
  const coordinatorId = adminPassword.startsWith('COORD:') ? adminPassword.slice(6) : '';
  const [students, setStudents]       = useState<StudentFull[]>([]);
  const [mentorNames, setMentorNames] = useState<Record<string, string>>({});
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [selected, setSelected]       = useState<StudentFull | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, mRes] = await Promise.all([getStudents(adminPassword), getAdminMentors(adminPassword)]);
      setStudents(sRes.students.filter(s => s.status === 'Active').sort((a, b) => a.name.localeCompare(b.name)));
      setMentorNames(mRes.mentors.reduce((acc, m) => ({ ...acc, [m.mentorId]: m.mentorName }), {} as Record<string, string>));
    } catch (err: any) { toast.error('Failed to load students: ' + err.message); }
    finally { setLoading(false); }
  }, [adminPassword]);

  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const visible = students.filter(s => !q
    || s.name.toLowerCase().includes(q)
    || s.studentId.toLowerCase().includes(q)
    || (s.course || '').toLowerCase().includes(q)
    || (mentorNames[s.mentorId] || '').toLowerCase().includes(q));

  return (
    <TrackerLayout>
      <div className="space-y-5">
        <div className="rounded-2xl overflow-hidden shadow-lg"
          style={{ background: 'linear-gradient(135deg,#2b2240 0%,#3d2f5f 50%,#2b2240 100%)' }}>
          <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl">🎓</span>
                <h1 className="text-xl font-bold text-white">Class Over</h1>
              </div>
              <p className="text-white/50 text-xs max-w-2xl">
                When a student has finished the course, raise Class Over for them here. They're made inactive straight away
                and admin is emailed their full class report.
              </p>
            </div>
            <button onClick={load}
              className="self-start sm:self-auto px-3 py-2 rounded-xl text-xs font-medium border border-white/20 text-white/70 hover:bg-white/10 transition-colors">
              ↻ Refresh
            </button>
          </div>
        </div>

        {!coordinatorId ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center text-sm text-gray-500">
            Class Over is raised by mentors and Student Coordinators — see the ones raised on the admin <b>Class Over</b> page.
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center gap-3">
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search by student, ID, course or mentor…"
                className="flex-1 px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#2b2240]" />
              <span className="text-xs text-gray-400">{visible.length} of {students.length} active students</span>
            </div>

            {loading ? (
              <div className="py-16 text-center text-sm text-gray-400">Loading students…</div>
            ) : visible.length === 0 ? (
              <div className="py-16 text-center text-sm text-gray-400">{q ? `No students match "${search}"` : 'No active students'}</div>
            ) : (
              <div className="divide-y divide-gray-50">
                {visible.map(s => (
                  <div key={s.studentId} className="px-4 py-3 flex items-center gap-3 flex-wrap hover:bg-gray-50/70">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center text-xs font-black flex-shrink-0"
                      style={{ background: '#f0edf7', color: '#2b2240' }}>
                      {initials(s.name)}
                    </div>
                    <div className="flex-1 min-w-[160px]">
                      <p className="text-sm font-bold text-gray-800">
                        {s.name} <span className="text-[11px] font-mono font-normal text-gray-400">{s.studentId}</span>
                      </p>
                      <p className="text-xs text-gray-500">
                        {s.course || '—'} · Mentor: {mentorNames[s.mentorId] || s.mentorId}
                        {s.dateJoined && <span className="text-gray-400"> · joined {s.dateJoined}</span>}
                      </p>
                    </div>
                    <button onClick={() => setSelected(s)}
                      className="px-3 py-2 rounded-xl text-xs font-bold border-2 transition-colors hover:bg-sky-50 flex-shrink-0"
                      style={{ borderColor: '#7dd3fc', color: '#0369a1' }}>
                      🎓 Raise Class Over
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {selected && (
        <ClassOverModal
          studentId={selected.studentId}
          studentName={selected.name}
          course={selected.course}
          raisedBy={coordinatorId}
          onDone={() => { setStudents(prev => prev.filter(s => s.studentId !== selected.studentId)); setSelected(null); }}
          onClose={() => setSelected(null)}
        />
      )}
    </TrackerLayout>
  );
};

export default CoordinatorClassOver;
